'use client';

import {
  useGetAccountLockQuery,
  useGetCanManageBillingPermissionQuery,
} from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useSupportLauncherId } from '@frameio/jarvis';
import { useTrackIntercomOpened } from '@frameio/plan-management/account-lock/hooks/useTrackIntercomOpened';
import { useResumePlan } from '@frameio/plan-management/hooks';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useAccountLockDialogContext } from '@frameio/shared/contexts/ModalContext/ModalContext';
import { AlertBanner } from '@frameio/vapor';
import { BannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/AccountLockBanner';
import { StyledA } from './StyledLink';
import { UniversalLink } from './UniversalLink';

const BILLING_PAGE_URL = '/settings/billing';
const USAGE_PAGE_URL = '/settings/usage';

TEXT.DEFINE(
  {
    /** Title when plan is paused  */
    titlePlanPaused: 'This account is unavailable while frozen.',

    /** Title when account is locked */
    titleAccountLocked: 'Account Locked',

    /** Message for billing managers when plan is paused */
    detailsPlanPausedWithPermission: (showResumePlanDialog: VoidFunction) => (
      <>
        To regain full access,{' '}
        <StyledA onClick={showResumePlanDialog}>reactivate the account</StyledA>
        .
      </>
    ),

    /** Message for non-billing users when plan is paused */
    detailsPlanPausedNoPermission: (ownerEmail: string) => (
      <>
        Contact account owner (
        <StyledA href={`mailto:${ownerEmail}`} target="_blank">
          {ownerEmail}
        </StyledA>
        ) for assistance.
      </>
    ),

    /** Message for Adobe-managed accounts with billing permissions */
    detailsAdobeWithPermission: ({
      launcherId,
      trackIntercomOpened,
    }: {
      launcherId: string;
      trackIntercomOpened: VoidFunction;
    }) => (
      <>
        <UniversalLink href={USAGE_PAGE_URL}>Review usage</UniversalLink> or{' '}
        <StyledA id={launcherId} onClick={trackIntercomOpened} rel="noreferrer">
          contact support
        </StyledA>{' '}
        for assistance.
      </>
    ),

    /** Message for Adobe-managed accounts without billing permissions */
    detailsAdobeNoPermission: 'Contact your admin for assistance.',

    /** Message for non-billing users */
    detailsNoPermission: (ownerEmail: string) => (
      <>
        Contact the account owner (
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          {ownerEmail}
        </UniversalLink>
        ) for assistance.
      </>
    ),

    /** Message when usage needs to be reduced */
    detailsReduceUsage: 'Reduce usage or upgrade to unlock',

    /** Message when payment needs to be updated */
    detailsUpdatePayment: () => (
      <>
        <UniversalLink href={BILLING_PAGE_URL}>
          Update payment details
        </UniversalLink>{' '}
        to resolve overdue balance.
      </>
    ),

    /** Default fallback message */
    detailsContactSupport: ({
      launcherId,
      trackIntercomOpened,
    }: {
      launcherId: string;
      trackIntercomOpened: VoidFunction;
    }) => (
      <>
        <StyledA id={launcherId} onClick={trackIntercomOpened} rel="noreferrer">
          Contact Support
        </StyledA>{' '}
        for assistance.
      </>
    ),
  },
  { StyledA, UniversalLink, USAGE_PAGE_URL, BILLING_PAGE_URL }
);

const getDetails = (
  canManageBilling: boolean,
  isDelinquent: boolean,
  isManagedByAdobe: boolean,
  isPlanPaused: boolean,
  isUnpaid: boolean,
  ownerEmail: string,
  showResumePlanDialog: VoidFunction,
  tier: string,
  trackIntercomOpened: VoidFunction,
  launcherId: string
) => {
  const isEnterprise = tier === 'ENTERPRISE';
  const isFree = tier === 'FREE';

  const isRetail = !isEnterprise;
  const isRetailAndNotFree = !isEnterprise && !isFree;

  const showReduceUsage =
    (isRetail && isDelinquent && !isUnpaid) ||
    (isFree && isDelinquent && isUnpaid);

  const showUpdatePayment =
    (isRetailAndNotFree && isDelinquent && isUnpaid) ||
    (isRetailAndNotFree && !isDelinquent && isUnpaid) ||
    (!isDelinquent && isUnpaid);

  if (isPlanPaused) {
    return canManageBilling
      ? TEXT.detailsPlanPausedWithPermission(showResumePlanDialog)
      : TEXT.detailsPlanPausedNoPermission(ownerEmail);
  }

  if (isManagedByAdobe) {
    return canManageBilling
      ? TEXT.detailsAdobeWithPermission({ launcherId, trackIntercomOpened })
      : TEXT.detailsAdobeNoPermission;
  }

  if (!canManageBilling) {
    return TEXT.detailsNoPermission(ownerEmail);
  }

  if (showReduceUsage) {
    return TEXT.detailsReduceUsage;
  }

  if (showUpdatePayment) {
    return TEXT.detailsUpdatePayment();
  }

  return TEXT.detailsContactSupport({ launcherId, trackIntercomOpened });
};

export const AccountLockBanner = () => {
  const accountId = useActiveAccountId();
  const launcherId = useSupportLauncherId();
  const trackIntercomOpened = useTrackIntercomOpened();

  const { data: accountLockData } = useGetAccountLockQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
  });

  const { data: canManageBillingData } = useGetCanManageBillingPermissionQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
  });

  const { isAccountLockDialogOpen } = useAccountLockDialogContext();

  const account = accountLockData?.account;
  const { lockedAt, lockReason } = account ?? {};
  const isAccountLocked = Boolean(lockedAt);
  const isPlanPaused = lockReason === 'PLAN_PAUSED';

  const { resumePlanDialog, showResumePlanDialog } = useResumePlan({
    skip: !isPlanPaused,
  });

  const canManageBilling =
    canManageBillingData?.account?.permissions?.canManageBilling ?? false;

  /** We do not show the account lock banner and modal at the same time. */
  const showBanner = isAccountLocked && !isAccountLockDialogOpen;

  if (!showBanner) return null;

  const isDelinquent = !!account?.delinquentAt;
  const isUnpaid = !!account?.unpaidAt;
  const ownerEmail = account?.owner?.email ?? '';
  const tier = account?.tier ?? 'PRO';
  const isManagedByAdobe = isAccountAdobeManaged(account?.managedBy);
  const variant = isPlanPaused ? 'notice' : 'error';

  const title = isPlanPaused ? TEXT.titlePlanPaused : TEXT.titleAccountLocked;

  const details = getDetails(
    canManageBilling,
    isDelinquent,
    isManagedByAdobe,
    isPlanPaused,
    isUnpaid,
    ownerEmail,
    showResumePlanDialog,
    tier,
    trackIntercomOpened,
    launcherId
  );

  return (
    <>
      <BannerWrapper>
        <AlertBanner details={details} title={title} variant={variant} />
      </BannerWrapper>

      {resumePlanDialog}
    </>
  );
};
